/**
 * Generic utilities for converting between object and array formats.
 *
 * The backend API uses object maps with string keys (e.g., { "approve": {...}, "reject": {...} })
 * while the form uses arrays with a 'key' property (e.g., [{ key: "approve", ...}, { key: "reject", ...}])
 *
 * Arrays are preferred for forms because:
 * - React Hook Form field arrays work with arrays
 * - Drag-and-drop reordering is simpler with arrays
 * - Array indices can be used for field names
 */
import { isString } from "lodash-es";

import { TemporaryFieldMarker } from "src/cases/templates/formUtils/types";
import { deepOmit } from "src/utils/objectUtils";

/**
 * Converts an object map to an array with 'key' property.
 *
 * @example
 * objectToKeyedArray({ approve: { name: "Approve" }, reject: { name: "Reject" } })
 * // => [{ key: "approve", name: "Approve" }, { key: "reject", name: "Reject" }]
 *
 * @param object - Object map with string keys
 * @returns Array with each value having a 'key' property
 */
export const objectToKeyedArray = <T extends object>(
  object: Record<string, T>,
): (T & { key: string })[] => {
  return Object.entries(object || {}).map(([key, value]) => ({
    ...value,
    key,
  }));
};

/**
 * Converts an array with 'key' property back to an object map.
 * Removes temporary form markers like _isNewlyAppended.
 *
 * @example
 * keyedArrayToObject([{ key: "approve", name: "Approve" }])
 * // => { approve: { name: "Approve" } }
 *
 * @example
 * // With excludeKey: false, the key is preserved in the value
 * keyedArrayToObject([{ key: "approve", name: "Approve" }], { excludeKey: false })
 * // => { approve: { key: "approve", name: "Approve" } }
 *
 * @param array - Array with 'key' property on each item
 * @param options.excludeKey - If true (default), removes 'key' from values
 * @returns Object map with original keys
 */
export const keyedArrayToObject = <
  T extends object & TemporaryFieldMarker & { key: string },
>(
  array: T[] | undefined,
  options = { excludeKey: true },
): Record<string, Omit<T, "key" | "_isNewlyAppended">> => {
  return Object.fromEntries(
    (array || [])
      .map(({ key, ...input }) => [
        key,
        {
          ...deepOmit(input, "_isNewlyAppended"),
          ...(options?.excludeKey ? {} : { key }),
        },
      ])
      .filter(([key]) => isString(key) && key.trim().length > 0),
  );
};
